import React, { useState, useEffect } from 'react';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../utils/config';
import '../styles/EditProductModal.css';

interface EditProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedSuppliers: any[];
  onSaveChanges: (updatedSuppliers: any[]) => void;
  onContinueToPreview: (updatedSuppliers: any[]) => void;
}

const EditProductModal: React.FC<EditProductModalProps> = ({ 
  isOpen, 
  onClose, 
  selectedSuppliers,
  onSaveChanges,
  onContinueToPreview
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [editableSuppliers, setEditableSuppliers] = useState<any[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{[key: string]: File[]}>({});
  const [uploadingFiles, setUploadingFiles] = useState<{[key: string]: boolean}>({});

  // Initialize editable suppliers when modal opens
  useEffect(() => {
    if (isOpen && selectedSuppliers) {
      console.log('🔍 DEBUG - EditProductModal - Initializing with selected suppliers:', selectedSuppliers);
      console.log('🔍 DEBUG - EditProductModal - Selected suppliers count:', selectedSuppliers.length);
      
      // Deep clone selected suppliers to make them editable
      const clonedSuppliers = selectedSuppliers.map(supplier => {
        // Handle different supplier types (local, ai, manual)
        let productName = '';
        let productBrand = '';
        let productDescription = '';
        let price = 0;
        let currency = 'CHF';
        
        if (supplier.supplierType === 'manual') {
          // Manual suppliers have products array
          productName = supplier.products?.[0]?.name || '';
          productDescription = supplier.products?.[0]?.description || '';
          price = supplier.products?.[0]?.price || 0;
          currency = supplier.currency || 'TRY';
          productBrand = supplier.product_brand || '';
        } else if (supplier.supplierType === 'local') {
          // Local suppliers have direct fields
          productName = supplier.product_name || '';
          productBrand = supplier.product_brand || '';
          productDescription = supplier.product_description || '';
          price = supplier.unit_price || 0;
          currency = supplier.currency || 'CHF';
        } else if (supplier.supplierType === 'ai') {
          // AI suppliers have products array
          productName = supplier.products?.[0]?.name || '';
          productDescription = supplier.products?.[0]?.description || '';
          price = supplier.products?.[0]?.price || 0;
          currency = supplier.currency || 'CHF';
          productBrand = supplier.product_brand || '';
        }
        
        return {
          ...supplier,
          // Ensure we have all necessary fields for editing
          product_name: productName,
          product_brand: productBrand,
          product_global_code: supplier.product_global_code || '',
          product_company_code: supplier.product_company_code || '',
          product_description: productDescription,
          price: price,
          currency: currency,
          delivery_time: supplier.delivery_time || '',
          minimum_order: supplier.minimum_order || '',
          payment_terms: supplier.payment_terms || '',
          notes: supplier.notes || ''
        };
      });
      
      console.log('🔍 DEBUG - EditProductModal - Cloned suppliers:', clonedSuppliers);
      setEditableSuppliers(clonedSuppliers);
      setHasChanges(false);
    } else {
      console.log('🔍 DEBUG - EditProductModal - Modal not open or no selected suppliers');
    }
  }, [isOpen, selectedSuppliers]);

  const handleFieldChange = (supplierIndex: number, field: string, value: any) => {
    const updatedSuppliers = [...editableSuppliers];
    const supplier = updatedSuppliers[supplierIndex];
    
    // Handle nested fields like products[0].price, products[0].currency, etc.
    if (field.includes('.')) {
      const parts = field.split('.');
      if (parts.length === 3 && parts[0] === 'products' && parts[1] === '0') {
        // Handle products[0].fieldName
        const productField = parts[2];
        if (supplier.products && supplier.products[0]) {
          supplier.products[0] = {
            ...supplier.products[0],
            [productField]: value
          };
        }
      }
    } else {
      // Handle top-level fields
      supplier[field] = value;
    }
    
    setEditableSuppliers(updatedSuppliers);
    setHasChanges(true);
    
    console.log('🔍 DEBUG - handleFieldChange:', {
      supplierIndex,
      field,
      value,
      updatedSupplier: supplier
    });
  };

  // Generate a valid MongoDB ObjectId-like string
  const generateObjectId = () => {
    const timestamp = Math.floor(Date.now() / 1000).toString(16);
    const randomPart = Math.random().toString(16).substring(2, 14);
    return timestamp + randomPart.padEnd(16, '0');
  };

  const handleFileUpload = (supplierIndex: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const supplier = editableSuppliers[supplierIndex];
    
    // Use existing valid ObjectId or generate a new one
    let supplierId = '';
    if (supplier._id && /^[0-9a-fA-F]{24}$/.test(supplier._id)) {
      // Valid MongoDB ObjectId
      supplierId = supplier._id;
    } else if (supplier.supplier_id && /^[0-9a-fA-F]{24}$/.test(supplier.supplier_id)) {
      // Valid MongoDB ObjectId
      supplierId = supplier.supplier_id;
    } else {
      // Generate a new ObjectId-like string
      supplierId = generateObjectId();
      // Store the generated ID in the supplier object
      editableSuppliers[supplierIndex]._id = supplierId;
    }
    
    console.log('🔍 DEBUG - handleFileUpload - Supplier:', supplier);
    console.log('🔍 DEBUG - handleFileUpload - Generated supplierId:', supplierId);
    console.log('🔍 DEBUG - handleFileUpload - Files:', files);
    
    if (files.length > 0) {
      setUploadedFiles(prev => ({
        ...prev,
        [supplierId]: [...(prev[supplierId] || []), ...files]
      }));
      setHasChanges(true);
    }
  };

  const handleRemoveFile = (supplierIndex: number, fileIndex: number) => {
    const supplier = editableSuppliers[supplierIndex];
    
    // Use existing valid ObjectId or generate a new one
    let supplierId = '';
    if (supplier._id && /^[0-9a-fA-F]{24}$/.test(supplier._id)) {
      // Valid MongoDB ObjectId
      supplierId = supplier._id;
    } else if (supplier.supplier_id && /^[0-9a-fA-F]{24}$/.test(supplier.supplier_id)) {
      // Valid MongoDB ObjectId
      supplierId = supplier.supplier_id;
    } else {
      // Generate a new ObjectId-like string
      supplierId = generateObjectId();
      // Store the generated ID in the supplier object
      editableSuppliers[supplierIndex]._id = supplierId;
    }
    
    setUploadedFiles(prev => ({
      ...prev,
      [supplierId]: prev[supplierId]?.filter((_, index) => index !== fileIndex) || []
    }));
    setHasChanges(true);
  };

  const uploadFilesToBackend = async (supplierIndex: number) => {
    const supplier = editableSuppliers[supplierIndex];
    
    // Use existing valid ObjectId or generate a new one
    let supplierId = '';
    if (supplier._id && /^[0-9a-fA-F]{24}$/.test(supplier._id)) {
      // Valid MongoDB ObjectId
      supplierId = supplier._id;
    } else if (supplier.supplier_id && /^[0-9a-fA-F]{24}$/.test(supplier.supplier_id)) {
      // Valid MongoDB ObjectId
      supplierId = supplier.supplier_id;
    } else {
      // Generate a new ObjectId-like string
      supplierId = generateObjectId();
      // Store the generated ID in the supplier object
      editableSuppliers[supplierIndex]._id = supplierId;
    }
    
    const supplierProductId = supplierId;
    const files = uploadedFiles[supplierId];
    
    if (!files || files.length === 0) return [];

    setUploadingFiles(prev => ({ ...prev, [supplierId]: true }));

    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('supplier_product_id', supplierProductId);
        formData.append('supplier_id', supplierId);
        formData.append('supplier_index', supplierIndex.toString());

        const response = await fetch(API_ENDPOINTS.UPLOAD_SUPPLIER_DOCUMENT, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }

        const result = await response.json();
        return result.data;
      });

      const uploadedFilesData = await Promise.all(uploadPromises);
      setUploadingFiles(prev => ({ ...prev, [supplierId]: false }));
      return uploadedFilesData;
    } catch (error) {
      console.error('Error uploading files:', error);
      setUploadingFiles(prev => ({ ...prev, [supplierId]: false }));
      throw error;
    }
  };

  const handleSaveChanges = async () => {
    try {
      // For temporary suppliers (manual/ai/local), we don't need to upload to backend
      // Just get the files from local state and attach them
      const updatedSuppliers = await Promise.all(editableSuppliers.map(async (supplier, index) => {
        const supplierId = supplier._id || supplier.supplier_id;
        const localFiles = uploadedFiles[supplierId] || [];
        
        // Convert local files to the format expected by attached_documents
        const supplierFiles = await Promise.all(localFiles.map(async (file, fileIndex) => {
          // Convert file to base64 for backend storage
          const base64Data = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              // Remove data:application/pdf;base64, prefix
              const base64 = result.split(',')[1];
              resolve(base64);
            };
            reader.readAsDataURL(file);
          });
          
          return {
            id: `temp_${supplierId}_${fileIndex}`,
            filename: file.name,
            original_name: file.name,
            size: file.size,
            type: file.type,
            uploaded_at: new Date().toISOString(),
            supplier_id: supplierId,
            file_data: base64Data  // Store as base64 for backend
          };
        }));
        
        console.log('🔍 DEBUG - handleSaveChanges - Supplier mapping:', {
          supplierIndex: index,
          supplierName: supplier.company_name,
          supplierId: supplierId,
          localFiles: localFiles,
          supplierFiles: supplierFiles
        });
        
        return {
          ...supplier,
          attached_documents: supplierFiles
        };
      }));

      console.log('✅ Supplier data updated locally (no backend update needed for temporary suppliers)');
      console.log('🔍 DEBUG - Updated suppliers:', updatedSuppliers);

      // Update local state with attached documents
      setEditableSuppliers(updatedSuppliers);
      onSaveChanges(updatedSuppliers);
      setHasChanges(false);
      
      // Return the updated suppliers
      return updatedSuppliers;
    } catch (error) {
      console.error('❌ Error updating supplier products:', error);
      alert('Error updating supplier products. Please try again.');
      return editableSuppliers; // Return current state on error
    }
  };

  const handleContinueToPreview = async () => {
    let suppliersToPass = editableSuppliers;
    
    if (hasChanges) {
      // Save changes and get updated suppliers
      console.log('🔍 DEBUG - handleContinueToPreview - Has changes, saving...');
      suppliersToPass = await handleSaveChanges();
      console.log('🔍 DEBUG - handleContinueToPreview - After save, suppliers:', suppliersToPass);
    }
    
    console.log('🔍 DEBUG - handleContinueToPreview - Final suppliers to pass:', suppliersToPass);
    console.log('🔍 DEBUG - handleContinueToPreview - First supplier attached_documents:', suppliersToPass[0]?.attached_documents);
    console.log('🔍 DEBUG - handleContinueToPreview - onContinueToPreview function:', onContinueToPreview);
    console.log('🔍 DEBUG - handleContinueToPreview - typeof onContinueToPreview:', typeof onContinueToPreview);
    
    // Pass the updated suppliers to the preview modal
    if (onContinueToPreview) {
      console.log('🔍 DEBUG - EditProductModal - Calling onContinueToPreview with:', suppliersToPass);
      console.log('🔍 DEBUG - EditProductModal - onContinueToPreview function:', onContinueToPreview);
      console.log('🔍 DEBUG - EditProductModal - onContinueToPreview function name:', onContinueToPreview.name);
      console.log('🔍 DEBUG - EditProductModal - onContinueToPreview function toString:', onContinueToPreview.toString());
      console.log('🔍 DEBUG - EditProductModal - onContinueToPreview function length:', onContinueToPreview.length);
      console.log('🔍 DEBUG - EditProductModal - onContinueToPreview function prototype:', onContinueToPreview.prototype);
      console.log('🔍 DEBUG - EditProductModal - onContinueToPreview function constructor:', onContinueToPreview.constructor);
      console.log('🔍 DEBUG - EditProductModal - onContinueToPreview function constructor name:', onContinueToPreview.constructor.name);
      console.log('🔍 DEBUG - EditProductModal - onContinueToPreview function call:', onContinueToPreview.call);
      console.log('🔍 DEBUG - EditProductModal - onContinueToPreview function apply:', onContinueToPreview.apply);
      console.log('🔍 DEBUG - EditProductModal - onContinueToPreview function bind:', onContinueToPreview.bind);
      onContinueToPreview(suppliersToPass);
      console.log('🔍 DEBUG - EditProductModal - onContinueToPreview called successfully');
    } else {
      console.error('❌ onContinueToPreview is not defined!');
    }
  };

  if (!isOpen || !editableSuppliers.length) return null;

  const comparisonData = [
    {
      label: t('common.supplier_name'),
      key: 'supplier_name',
      getValue: (supplier: any) => supplier.supplier_name || supplier.company_name || supplier.name,
      type: 'text',
      editable: false
    },
    {
      label: t('common.product_name'),
      key: 'product_name',
      getValue: (supplier: any) => supplier.product_name,
      type: 'text',
      editable: true
    },
    {
      label: t('common.product_brand'),
      key: 'product_brand',
      getValue: (supplier: any) => supplier.product_brand || 'N/A',
      type: 'text',
      editable: true
    },
    {
      label: t('common.product_global_code'),
      key: 'product_global_code',
      getValue: (supplier: any) => supplier.product_global_code || '',
      type: 'text',
      editable: true
    },
    {
      label: t('common.product_company_code'),
      key: 'product_company_code',
      getValue: (supplier: any) => supplier.product_company_code || '',
      type: 'text',
      editable: true
    },
    {
      label: t('common.product_description'),
      key: 'product_description',
      getValue: (supplier: any) => supplier.product_description || '',
      type: 'textarea',
      editable: true
    },
        {
          label: t('common.price'),
          key: (supplier: any) => {
            // For AI suppliers, use products[0].price, for others use price
            if (supplier.supplierType === 'ai' || (supplier.products && supplier.products[0])) {
              return 'products.0.price';
            }
            return 'price';
          },
          getValue: (supplier: any) => supplier.price || supplier.unit_price || 0,
          type: 'number',
          editable: true
        },
    {
      label: t('common.currency'),
      key: 'currency',
      getValue: (supplier: any) => supplier.currency || 'CHF',
      type: 'select',
      editable: true,
      options: ['CHF', 'EUR', 'USD', 'TRY']
    },
    {
      label: t('common.delivery_time'),
      key: 'delivery_time',
      getValue: (supplier: any) => supplier.delivery_time || '',
      type: 'text',
      editable: true
    },
    {
      label: t('common.minimum_order'),
      key: 'minimum_order',
      getValue: (supplier: any) => supplier.minimum_order || '',
      type: 'text',
      editable: true
    },
    {
      label: t('common.payment_terms'),
      key: 'payment_terms',
      getValue: (supplier: any) => supplier.payment_terms || '',
      type: 'text',
      editable: true
    },
    {
      label: t('common.notes'),
      key: 'notes',
      getValue: (supplier: any) => supplier.notes || '',
      type: 'textarea',
      editable: true
    },
    {
      label: t('common.attached_documents'),
      key: 'attached_documents',
      getValue: (supplier: any) => supplier.attached_documents || [],
      type: 'file_upload',
      editable: true
    }
  ];

  const renderEditableField = (supplier: any, supplierIndex: number, field: any) => {
    if (!field.editable) {
      return <span>{field.getValue(supplier)}</span>;
    }

    // Get the actual key (handle dynamic keys)
    const actualKey = typeof field.key === 'function' ? field.key(supplier) : field.key;
    
    // Get value based on the key (handle nested fields)
    let value = '';
    if (actualKey.includes('.')) {
      const parts = actualKey.split('.');
      if (parts.length === 3 && parts[0] === 'products' && parts[1] === '0') {
        value = supplier.products?.[0]?.[parts[2]] || '';
      }
    } else {
      value = supplier[actualKey] || '';
    }
    
    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(supplierIndex, actualKey, e.target.value)}
            className="edit-textarea"
            rows={2}
          />
        );
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(supplierIndex, actualKey, e.target.value)}
            className="edit-select"
          >
            {field.options.map((option: string) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(supplierIndex, actualKey, parseFloat(e.target.value) || 0)}
            className="edit-input"
            step="0.01"
            min="0"
          />
        );
      case 'file_upload':
        // Use existing valid ObjectId or generate a new one
        let supplierId = '';
        if (supplier._id && /^[0-9a-fA-F]{24}$/.test(supplier._id)) {
          // Valid MongoDB ObjectId
          supplierId = supplier._id;
        } else if (supplier.supplier_id && /^[0-9a-fA-F]{24}$/.test(supplier.supplier_id)) {
          // Valid MongoDB ObjectId
          supplierId = supplier.supplier_id;
        } else {
          // Generate a new ObjectId-like string
          supplierId = generateObjectId();
          // Store the generated ID in the supplier object
          editableSuppliers[supplierIndex]._id = supplierId;
        }
        
        const files = uploadedFiles[supplierId] || [];
        const isUploading = uploadingFiles[supplierId] || false;
        
        return (
          <div className="file-upload-section">
            <label className={`file-input-label ${isUploading ? 'disabled' : ''}`}>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                multiple
                onChange={(e) => handleFileUpload(supplierIndex, e)}
                className="file-input"
                disabled={isUploading}
                style={{ display: 'none' }}
              />
              <div className="file-input-display">
                📁 {isUploading ? t('common.uploading') + '...' : (t('common.click_to_upload_files') || 'Click to upload files')}
              </div>
            </label>
            {files.length > 0 && (
              <div className="uploaded-files">
                {files.map((file, fileIndex) => (
                  <div key={fileIndex} className="file-item">
                    <span className="file-name">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(supplierIndex, fileIndex)}
                      className="remove-file-btn"
                      disabled={isUploading}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {isUploading && (
              <div className="uploading-indicator">
                {t('common.uploading')}...
              </div>
            )}
          </div>
        );
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(supplierIndex, field.key, e.target.value)}
            className="edit-input"
          />
        );
    }
  };

  return (
    <div className="edit-product-modal-overlay" onClick={onClose}>
      <div className="edit-product-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="edit-product-modal-close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <div className="edit-product-modal-header">
          <h2>{t('common.edit_product_details')}</h2>
          <p className="edit-product-modal-subtitle">
            {t('common.edit_supplier_product_information')} ({editableSuppliers.length} {t('common.suppliers')})
          </p>
        </div>

        <div className="edit-product-container">
          <div className="edit-product-table-wrapper">
            <table className="edit-product-table">
              <thead>
                <tr>
                  <th className="metric-column">{t('common.metrics')}</th>
                  {editableSuppliers.map((supplier: any, index: number) => (
                    <th key={`${supplier._id || supplier.product_id || supplier.supplier_id || supplier.id || index}-header`} className="supplier-column">
                      <div className="supplier-header">
                        <span className="supplier-name">{supplier.supplier_name || supplier.name}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((metric, index) => (
                  <tr key={String(metric.key)} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                    <td className="metric-label">{metric.label}</td>
                    {editableSuppliers.map((supplier: any, supplierIndex: number) => (
                      <td key={`${supplier._id || supplier.product_id || supplier.supplier_id || supplier.id || supplierIndex}-${String(metric.key)}`} className="metric-value">
                        {renderEditableField(supplier, supplierIndex, metric)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="edit-product-modal-footer">
          <button className="edit-product-modal-btn secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          {hasChanges && (
            <button className="edit-product-modal-btn secondary" onClick={handleSaveChanges}>
              {t('common.save_changes')}
            </button>
          )}
          <button 
            className="edit-product-modal-btn primary"
            onClick={handleContinueToPreview}
          >
            {t('common.continue_to_preview')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProductModal;
